import { css } from '@emotion/react';
import { faSync } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { TextInputComponent } from '~/_shared/baseComponents/inputs';
import { RadioGroupComponent } from '~/_shared/baseComponents/radio/radioGroup.component';
import {
  createColor, createColorWithOpacity, guaranteeHash,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { calculateGradientColor } from '~/_shared/utils/colors/colors.helpers';
import { type HexColor } from '~/_shared/utils/colors/colors.types';
import { useTranslation } from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import {
  formatValue, RangeType,
} from '~/_shared/utils/range/range.helpers';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { BoundaryTerritoryListingLimit } from '~/boundary/constants';
import { useLoadMoreListing } from '~/boundary/hooks/useLoadMoreListing';
import { BoundarySettingsLoadMoreRowComponent } from '~/boundary/settings/table/row/BoundarySettingsLoadMoreRow.component';
import { isBoundaryTerritoryEmpty } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import type { BoundaryTerritoryId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryTerritoryAssignments } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import {
  type BoundaryTerritory, type BoundaryTerritoryGroup, type BoundaryTerritoryGroupStyle,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { BOUNDARY_SETTINGS_MIN_RANGES_COUNT } from '../../../../../boundary/settings/boundarySettings.common';
import {
  BOUNDARY_GROUP_TIE_ID, BOUNDARY_MAX_VALUE_ID, BOUNDARY_MIN_VALUE_ID, BOUNDARY_NO_DATA_ID, bucketTypeToRangeType,
  isBoundaryTerritoryHidden, isBoundaryTerritorySpecial, isBoundaryTerritorySpecialOrCustom, rangeTypeToBucketType,
  translateBoundaryTerritoryDisplayName,
} from '../../../../../boundary/settings/boundarySettings.helpers';
import { BoundarySettingsCustomTerritoryDataComponent } from '../../../../../boundary/settings/custom/boundarySettingsCustomTerritoryDataComponent';
import {
  getBoundaryTerritoryGroupSettingsStyleDefaults, restoreBTGDefaultColors,
} from '../../../../../boundary/settings/defaultBoundaryTerritoryStyles';
import { NumberOfRangesComponent } from '../../../../../boundary/settings/numberOfRanges/numberOfRanges.component';
import {
  getBoundaryMaxRange, getBoundaryTerritoryRangeValues,
  handleBoundaryTerritoryValueToChange, recalculateBoundaryTerritoriesValues, validateBoundarySettingsNumericValue,
} from '../../../../../boundary/settings/numeric/boundarySettingsNumeric.helpers';
import { RangeColorsComponent } from '../../../../../boundary/settings/rangeColors/rangeColors.component';
import { BoundarySettingsRestoreButtonComponent } from '../../../../../boundary/settings/restoreButton/boundarySettingsRestoreButton.component';
import { BoundarySettingsTableComponent } from '../../../../../boundary/settings/table/boundarySettingsTable.component';
import { BoundarySettingsTableRowComponent } from '../../../../../boundary/settings/table/row/boundarySettingsTableRow.component';
import { DEFAULT_RANGES_COUNT } from '../../../../../boundary/settings/useBoundaryFillSettingsPropsVariants';

const boundarySettingsWrapperStyle = css({
  display: 'flex',
  gap: 20,
  padding: '20px 24px',
});

const boundaryColumnStyle = css({
  flex: 1,
});

const rangeTypeRadioGroupStyle = css({
  gap: 25,
  marginTop: 25,
});

const dataLabelStyle = css({
  textTransform: 'uppercase',
});

const tablePrimaryDataStyle = css({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

const tableToLabelStyle = css({
  padding: '0 5px',
});

const tableInputStyle = ({ theme, isValid }: ThemeProps<{ isValid: boolean }>) => css({
  background: theme.backgroundColors.secondary,
  minWidth: 50,
  borderColor: isValid ? undefined : theme.borderColors.error,
});

const toValueStyle = css({
  maxWidth: 120,
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

const fromValueStyle = css({
  maxWidth: 120,
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

const valueToWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
});

const boundarySettingsActionsWrapperStyle = css({
  padding: '5px 24px 20px',
  display: 'flex',
  justifyContent: 'space-between',
});

const addTopsideDelimiterStyle = (theme: Theme) => css({
  borderTop: `1px solid ${theme.borderColors.primary}`,
});

const restoreColorsButtonStyle = css({
  padding: 0,
});

const containerStyle = css({
  width: '100%',
});

const footerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 10,
});

type BoundarySettingsNumericWithoutCommonProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  valueIsInitial: boolean;
  disableRangeTypeChange: boolean;
  isValid: boolean;
  isDecimal: boolean;
  max: number | typeof BOUNDARY_MAX_VALUE_ID;
  min: number | typeof BOUNDARY_MIN_VALUE_ID;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  onNumberOfRangesChange: (numberOfRanges: number, style: BoundaryTerritoryGroupStyle, rangeType: RangeType) => void;
  onRecalculateRangesClick: () => void;
  setIsFormValid: (isFormValid: boolean) => void;
}>;

export const BoundaryFillSettingsNumericComponent: FC<BoundarySettingsNumericWithoutCommonProps> = (props) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const { boundaryTerritoryGroup, onBoundaryTerritoryGroupChange } = props;
  const setIsFormValidRef = useRef(props.setIsFormValid);
  const style = boundaryTerritoryGroup.settings.style;
  const boundaryTerritories = boundaryTerritoryGroup.settings.boundaryTerritories;

  const lineWidth = style.lineWidth;

  const rangeType = bucketTypeToRangeType(boundaryTerritoryGroup.settings.bucketType) ?? RangeType.Value;
  const prevRangeType = usePrevious(rangeType);

  const [{ adjustedMin, adjustedMax }, setAdjustedMinMax] = useState(adjustRangeMinMaxForExistingBoundaryTerritories(
    props.min,
    props.max,
    boundaryTerritories));

  const rangeMin = rangeType === RangeType.Value ? adjustedMin : 0;
  const rangeMax = rangeType === RangeType.Value ? adjustedMax : 100;
  const maxNumberOfRanges = useMemo(() => {
    if (props.isDecimal) {
      return 10;
    }

    const boundaryMaxRange = getBoundaryMaxRange(rangeMin, rangeMax);
    return Math.max(boundaryMaxRange, BOUNDARY_SETTINGS_MIN_RANGES_COUNT);
  }, [props.isDecimal, rangeMin, rangeMax]);
  const numberOfRanges = boundaryTerritoryGroup.settings.rangesCount ?? DEFAULT_RANGES_COUNT;

  const prevRangeMin = usePrevious(rangeMin);
  const prevRangeMax = usePrevious(rangeMax);

  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();

  const isDecimalMode = props.isDecimal && rangeType === RangeType.Value;
  const dataTableValueSuffix = rangeType === RangeType.Percentage ? '%' : '';

  const handleChangeTerritoryValue = useCallback((territories: ReadonlyArray<BoundaryTerritory>) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: territories,
      },
    });
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const {
    limit: boundarySettingListCountLimit,
    increaseLimit: increaseBoundarySettingListCountLimit,
  } = useLoadMoreListing(BoundaryTerritoryListingLimit);

  const handleChangeRangeType = (rangeType: RangeType) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        bucketType: rangeTypeToBucketType(rangeType),
      },
    });
  };

  const handleRestoreDefaultColors = () => {
    const gradientColors = getBoundaryTerritoryGroupSettingsStyleDefaults().gradientColors;
    let newBoundaryTerritories: ReadonlyArray<BoundaryTerritory> = resetSpecialBoundaryTerritoriesColors(
      boundaryTerritoryGroup.settings.boundaryTerritories);
    newBoundaryTerritories = recalculateBoundaryTerritoryColors(
      newBoundaryTerritories,
      gradientColors
    );

    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: newBoundaryTerritories,
        style: {
          ...restoreBTGDefaultColors(boundaryTerritoryGroup).settings.style,
          gradientColors,
        },
      },
    });
  };

  const recalculateBoundaryTerritoryColors = (
    boundaryTerritories: ReadonlyArray<BoundaryTerritory>,
    gradientColors: BoundaryTerritoryGroupStyle['gradientColors'],
  ): ReadonlyArray<BoundaryTerritory> => {
    const numericalTerritories = boundaryTerritories.filter(bt => !isBoundaryTerritorySpecialOrCustom(bt));
    const specialTerritories = boundaryTerritories.filter(bt => isBoundaryTerritorySpecialOrCustom(bt));

    const { minimal, low, medium, high } = gradientColors;
    const colorsToUse: NonEmptyArray<HexColor> = [minimal.color, low.color, medium.color, high.color];

    const newNumericalTerritories = numericalTerritories.map((bt, index) => ({
      ...bt,
      style: {
        ...bt.style,
        color: calculateGradientColor({
          position: index,
          totalCount: numericalTerritories.length,
          colors: colorsToUse,
        }),
        opacity: 50,
      },
    }));

    return newNumericalTerritories.concat(specialTerritories);
  };

  const resetSpecialBoundaryTerritoriesColors = (boundaryTerritories: ReadonlyArray<BoundaryTerritory>) => boundaryTerritories.map(
    bt => {
      if (!isBoundaryTerritorySpecial(bt)) {
        return bt;
      }

      let style: { color: HexColor; opacity: number };

      switch (bt.boundaryTerritoryId) {
        case BOUNDARY_GROUP_TIE_ID: {
          style = { color: '#000', opacity: 50 };
          break;
        }
        case BOUNDARY_NO_DATA_ID: {
          style = { color: '#ccc', opacity: 50 };
          break;
        }
        default:
          style = { color: '#fff', opacity: 0 };
      }

      return {
        ...bt,
        style: {
          ...bt.style,
          ...style,
        },
      };
    });

  const updateGroupGradientColors = (gradientColors: BoundaryTerritoryGroupStyle['gradientColors']) => {
    const newBoundaryTerritories = recalculateBoundaryTerritoryColors(
      boundaryTerritoryGroup.settings.boundaryTerritories, gradientColors
    );

    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: newBoundaryTerritories,
        style: {
          ...boundaryTerritoryGroup.settings.style,
          gradientColors,
        },
      },
    });
  };

  const handleChangeLowValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      low: { color: color.hex },
    });
  };

  const handleChangeMediumValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      medium: { color: color.hex },
    });
  };

  const handleChangeHighValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      high: { color: color.hex },
    });
  };

  const handleChangeMinimalValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      minimal: { color: color.hex },
    });
  };

  const handleChangeBoundaryTerritoryColor = useCallback((id: BoundaryTerritoryId) => (color: ColorResult) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: boundaryTerritories.map((bt) => {
          if (id !== bt.boundaryTerritoryId) {
            return bt;
          }
          else {
            return {
              ...bt,
              style: {
                ...bt.style,
                color: guaranteeHash(color.hex),
                opacity: color.rgba.a * 100,
              },
            };
          }
        }),
      },
    });
  }, [boundaryTerritories, boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const handleChangeBoundaryTerritoryName = (id: BoundaryTerritoryId, name: string) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: boundaryTerritoryGroup.settings.boundaryTerritories.map(
          bt => bt.boundaryTerritoryId === id ?
            {
              ...bt,
              displayName: name,
              translate: false,
            } : bt,
        ),
      },
    });
  };

  const handleNumberOfRangesChange = (numberOfRanges: number) => {
    props.onNumberOfRangesChange(numberOfRanges, style, rangeType);
  };

  // validate boundary territories, notify parent on validation results
  useEffect(() => {
    for (let i = 0; i < boundaryTerritories.length; i++) {
      const bt = boundaryTerritories[i];
      if (!bt) {
        return;
      }

      if (!validateBoundarySettingsNumericValue(
        bt,
        boundaryTerritories[i - 1] ?? null,
        boundaryTerritories[i + 1] ?? null,
        rangeMin,
        rangeMax,
      )) {
        setIsFormValidRef.current(false);
        return;
      }

      if (isTextEmpty(bt.displayName)) {
        setIsFormValidRef.current(false);
        return;
      }

      setIsFormValidRef.current(true);
    }
  }, [boundaryTerritories, rangeMax, rangeMin]);

  // recalculate range min / range max when min or max props are changed
  const boundaryTerritoriesRef = useRef(boundaryTerritories);
  boundaryTerritoriesRef.current = boundaryTerritories;
  useEffect(() => {
    const adjustedBounds = rangeType !== prevRangeType
      ? { adjustedMax: props.max, adjustedMin: props.min }
      : adjustRangeMinMaxForExistingBoundaryTerritories(props.min, props.max, boundaryTerritoriesRef.current);
    setAdjustedMinMax(adjustedBounds);
  }, [prevRangeType, props.max, props.min, rangeType]);

  // recalculate boundary territories values on range type change
  useEffect(() => {
    if (props.valueIsInitial) {
      return;
    }

    if (prevRangeMin === undefined || prevRangeMax === undefined) {
      return;
    }

    if (prevRangeMin === BOUNDARY_MIN_VALUE_ID || prevRangeMax === BOUNDARY_MAX_VALUE_ID) {
      return;
    }

    if (props.min === BOUNDARY_MIN_VALUE_ID || props.max === BOUNDARY_MAX_VALUE_ID) {
      return;
    }

    if (rangeType === prevRangeType) {
      return;
    }

    if (rangeType === RangeType.Value) {
      // was change from RangeType.Percentage to RangeType.Value so prev min was 0 and prev max was 100
      handleChangeTerritoryValue(recalculateBoundaryTerritoriesValues(boundaryTerritories,
        props.min,
        props.max,
        0,
        100,
        isDecimalMode));
    }
    else {
      handleChangeTerritoryValue(recalculateBoundaryTerritoriesValues(boundaryTerritories,
        0,
        100,
        prevRangeMin,
        prevRangeMax,
        isDecimalMode));
    }
  },
  [rangeMin, rangeMax, prevRangeMin, prevRangeMax, rangeType, isDecimalMode, handleChangeTerritoryValue,
    boundaryTerritories,
    prevRangeType, props.min, props.max, props.valueIsInitial]);

  return (
    <div css={containerStyle}>
      <div css={boundarySettingsWrapperStyle}>
        <div css={boundaryColumnStyle}>
          <RangeColorsComponent
            highValueColor={createColor(style.gradientColors.high.color)}
            onHighValueColorChange={handleChangeHighValueColor}
            mediumValueColor={createColor(style.gradientColors.medium.color)}
            onMediumValueColorChange={handleChangeMediumValueColor}
            lowValueColor={createColor(style.gradientColors.low.color)}
            onLowValueColorChange={handleChangeLowValueColor}
            minimalValueColor={createColor(style.gradientColors.minimal.color)}
            onMinimalValueColorChange={handleChangeMinimalValueColor}
          />
        </div>

        <div css={boundaryColumnStyle}>
          <NumberOfRangesComponent
            maxNumberOfRanges={maxNumberOfRanges}
            numberOfRanges={Math.min(numberOfRanges, maxNumberOfRanges)}
            onNumberOfRangesChange={handleNumberOfRangesChange}
          />

          {!props.disableRangeTypeChange && (
            <RadioGroupComponent
              css={rangeTypeRadioGroupStyle}
              selectedValue={rangeType}
              onValueChange={handleChangeRangeType}
              items={[{
                value: RangeType.Percentage,
                label: t('Percentage Ranges'),
              }, {
                value: RangeType.Value,
                label: t('Value Ranges'),
              }]}
            />
          )}
        </div>
      </div>

      <div css={boundarySettingsActionsWrapperStyle}>
        <BoundarySettingsRestoreButtonComponent
          css={restoreColorsButtonStyle}
          prefixIcon={faSync}
          text={t('Restore Default Colors')}
          onClick={handleRestoreDefaultColors}
        />
      </div>

      {(boundaryTerritories.length > 0) && (
        <BoundarySettingsTableComponent
          css={addTopsideDelimiterStyle}
          dataHeadingLabel={t('Number Ranges')}
          dataRows={(
            <>
              {boundaryTerritories.map((item, index) => {
                const isEmpty = isBoundaryTerritoryEmpty(boundaryTerritoryAssignments,
                  props.boundaryTerritoryGroup.boundaryTerritoryGroupId,
                  item);
                const isHidden = isBoundaryTerritoryHidden(item,
                  isEmpty,
                  boundaryTerritoryGroup.settings.boundaryTerritoryType);
                if (isHidden || (index >= boundarySettingListCountLimit)) {
                  return null;
                }

                const values = getBoundaryTerritoryRangeValues(item);
                const fromValue = values && values.valueFrom !== BOUNDARY_MIN_VALUE_ID ? values.valueFrom : rangeMin;
                const toValue = values && values.valueTo !== BOUNDARY_MAX_VALUE_ID ? values.valueTo : rangeMax;

                const fromValueFormatted = fromValue !== BOUNDARY_MIN_VALUE_ID ?
                  formatValue(fromValue.toString()) + dataTableValueSuffix :
                  t('min');
                const toValueFormatted = toValue !== BOUNDARY_MAX_VALUE_ID ?
                  formatValue(toValue.toString()) + dataTableValueSuffix :
                  t('max');
                const isSpecial = isBoundaryTerritorySpecial(item);

                const isRowValid = validateBoundarySettingsNumericValue(
                  item,
                  boundaryTerritories[index - 1] ?? null,
                  boundaryTerritories[index + 1] ?? null,
                  rangeMin,
                  rangeMax,
                );

                const color = createColorWithOpacity(
                  item.style.color,
                  item.style.opacity / 100,
                );

                return (
                  <BoundarySettingsTableRowComponent
                    key={item.boundaryTerritoryId}
                    index={index + 1}
                    data={item.custom ? (
                      <BoundarySettingsCustomTerritoryDataComponent
                        territory={item}
                        onTerritoryNameChange={handleChangeBoundaryTerritoryName}
                      />
                    ) : isSpecial ? (
                      <span css={dataLabelStyle}>{translateBoundaryTerritoryDisplayName(item, t)}</span>
                    ) : (
                      <div css={tablePrimaryDataStyle}>
                        <span
                          title={fromValueFormatted}
                          css={fromValueStyle}
                        >
                          {t('{{value}}+', { value: fromValueFormatted })}
                        </span>
                        <div css={valueToWrapperStyle}>
                          <span css={tableToLabelStyle}>{t('toNumber')}</span>
                          {values && values.valueTo !== BOUNDARY_MAX_VALUE_ID ? (
                            <TextInputComponent
                              css={tableInputStyle({ theme, isValid: isRowValid })}
                              onChange={value => handleChangeTerritoryValue(
                                handleBoundaryTerritoryValueToChange(index,
                                  value,
                                  boundaryTerritories,
                                  rangeMin,
                                  rangeMax,
                                  isDecimalMode))}
                              value={values.valueTo}
                              title={toValueFormatted}
                              icon={null}
                            />
                          ) : (
                            <span
                              title={toValueFormatted}
                              css={toValueStyle}
                            >
                              {toValueFormatted}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    lineColor={createColor(style.lineColor)}
                    lineWidth={lineWidth}
                    color={color}
                    onColorChange={handleChangeBoundaryTerritoryColor(item.boundaryTerritoryId)}
                  />
                );
              })}
              {(boundaryTerritories.length > boundarySettingListCountLimit) && (
                <BoundarySettingsLoadMoreRowComponent onClick={increaseBoundarySettingListCountLimit} />
              )}
            </>
          )}
        />
      )}
      <div css={[footerStyle, addTopsideDelimiterStyle]}>
        <BoundarySettingsRestoreButtonComponent
          prefixIcon={faSync}
          text={t('Recalculate Equal Ranges')}
          onClick={props.onRecalculateRangesClick}
        />
      </div>
    </div>
  );
};

const adjustRangeMinMaxForExistingBoundaryTerritories = (
  min: number | typeof BOUNDARY_MIN_VALUE_ID,
  max: number | typeof BOUNDARY_MAX_VALUE_ID,
  boundaryTerritories: readonly BoundaryTerritory[],
) => {
  const rangeBoundaryTerritories = boundaryTerritories.filter(t => !isBoundaryTerritorySpecial(t));

  if (!rangeBoundaryTerritories.length) {
    return { adjustedMin: min, adjustedMax: max };
  }

  let adjustedMin = min;

  if (min !== BOUNDARY_MIN_VALUE_ID) {
    const first = rangeBoundaryTerritories[0];
    const firstValues = first && getBoundaryTerritoryRangeValues(first);
    const valueTo: number = notNullsy(firstValues?.valueTo) ? Number(firstValues.valueTo) : NaN;
    adjustedMin = !isNaN(valueTo) && valueTo < min ? valueTo : min;
  }

  let adjustedMax = max;

  if (max !== BOUNDARY_MAX_VALUE_ID) {
    const last = rangeBoundaryTerritories.lastItem;
    const lastValues = getBoundaryTerritoryRangeValues(last);
    const valueFrom: number = notNullsy(lastValues?.valueFrom) ? Number(lastValues.valueFrom) : NaN;
    adjustedMax = !isNaN(valueFrom) && valueFrom > max ? valueFrom : max;
  }

  return { adjustedMin, adjustedMax };
};
